import React, { useState, useEffect } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { v4 as uuidv4 } from 'uuid';

// Helper to ensure option has ID
const ensureOptionId = (option) => ({
  ...option,
  id: option.id || uuidv4(),
  values: option.values.map((v, i) => 
    typeof v === 'object' ? { ...v, id: v.id || uuidv4() } : { value: v, id: uuidv4() }
  )
});

// Variant Manager Component for Admin Dashboard
const VariantManager = ({ product, onUpdate }) => {
  const [variantOptions, setVariantOptions] = useState(
    (product?.variant_options || []).map(ensureOptionId)
  );
  const [variants, setVariants] = useState(product?.variants || []);
  const [hasVariants, setHasVariants] = useState(product?.has_variants || false);

  // Add a new option type (e.g., "Color", "Size")
  const addOptionType = () => {
    setVariantOptions([...variantOptions, { id: uuidv4(), name: '', values: [{ id: uuidv4(), value: '' }] }]);
  };

  // Update option type name
  const updateOptionName = (optionId, name) => {
    const updated = variantOptions.map(opt => 
      opt.id === optionId ? { ...opt, name } : opt
    );
    setVariantOptions(updated);
  };

  // Add value to option type
  const addOptionValue = (optionId) => {
    const updated = variantOptions.map(opt =>
      opt.id === optionId 
        ? { ...opt, values: [...opt.values, { id: uuidv4(), value: '' }] }
        : opt
    );
    setVariantOptions(updated);
  };

  // Update option value
  const updateOptionValue = (optionId, valueId, value) => {
    const updated = variantOptions.map(opt =>
      opt.id === optionId
        ? { ...opt, values: opt.values.map(v => v.id === valueId ? { ...v, value } : v) }
        : opt
    );
    setVariantOptions(updated);
  };

  // Remove option value
  const removeOptionValue = (optionId, valueId) => {
    const updated = variantOptions.map(opt =>
      opt.id === optionId
        ? { ...opt, values: opt.values.filter(v => v.id !== valueId) }
        : opt
    );
    setVariantOptions(updated);
  };

  // Remove option type
  const removeOptionType = (optionId) => {
    const updated = variantOptions.filter(opt => opt.id !== optionId);
    setVariantOptions(updated);
  };

  // Generate all variant combinations
  const generateVariants = () => {
    if (variantOptions.length === 0) return;
    
    // Filter out empty options and extract values
    const validOptions = variantOptions.filter(opt => opt.name && opt.values.some(v => v.value));
    if (validOptions.length === 0) return;

    // Generate combinations
    const combinations = [];
    const generateCombinations = (current, optionIndex) => {
      if (optionIndex >= validOptions.length) {
        combinations.push({ ...current });
        return;
      }
      const option = validOptions[optionIndex];
      for (const valueObj of option.values.filter(v => v.value)) {
        generateCombinations({ ...current, [option.name]: valueObj.value }, optionIndex + 1);
      }
    };
    generateCombinations({}, 0);

    // Create variant objects
    const newVariants = combinations.map(combo => {
      const existingVariant = variants.find(v => 
        JSON.stringify(v.options) === JSON.stringify(combo)
      );
      return existingVariant || {
        id: uuidv4(),
        sku: `${product?.sku || 'NEW'}-${Object.values(combo).join('-').toUpperCase().replace(/\s+/g, '')}`,
        options: combo,
        price: product?.price || 0,
        compare_at_price: null,
        quantity_in_stock: 50,
        image_url: ''
      };
    });

    setVariants(newVariants);
  };

  // Update single variant
  const updateVariant = (variantId, field, value) => {
    const updated = variants.map(v => 
      v.id === variantId ? { ...v, [field]: value } : v
    );
    setVariants(updated);
  };

  // Remove variant
  const removeVariant = (variantId) => {
    setVariants(variants.filter(v => v.id !== variantId));
  };

  // Save changes
  const handleSave = () => {
    // Convert back to simple format for backend
    const simplifiedOptions = variantOptions.map(opt => ({
      name: opt.name,
      values: opt.values.map(v => v.value)
    }));
    
    onUpdate({
      has_variants: hasVariants,
      variant_options: simplifiedOptions,
      variants: variants
    });
  };

  return (
    <div className="space-y-6 border-t pt-6 mt-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-text-primary">Product Variants</h3>
          <p className="text-sm text-text-muted">Add options like color, size, quantity pack</p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={hasVariants}
            onChange={(e) => setHasVariants(e.target.checked)}
            className="w-5 h-5 rounded border-gray-300"
          />
          <span className="text-sm font-medium">Enable Variants</span>
        </label>
      </div>

      {hasVariants && (
        <>
          {/* Option Types */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Option Types</Label>
              <Button type="button" variant="outline" size="sm" onClick={addOptionType} className="rounded-full">
                <Plus className="w-4 h-4 mr-1" /> Add Option
              </Button>
            </div>

            {variantOptions.map((option) => (
              <div key={option.id} className="border rounded-xl p-4 space-y-3 bg-sand/30">
                <div className="flex items-center gap-3">
                  <Input
                    placeholder="Option name (e.g., Color, Size)"
                    value={option.name}
                    onChange={(e) => updateOptionName(option.id, e.target.value)}
                    className="rounded-xl flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeOptionType(option.id)}
                    className="text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {option.values.map((valueObj) => (
                    <div key={valueObj.id} className="flex items-center gap-1">
                      <Input
                        placeholder="Value"
                        value={valueObj.value}
                        onChange={(e) => updateOptionValue(option.id, valueObj.id, e.target.value)}
                        className="rounded-xl w-32"
                      />
                      {option.values.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeOptionValue(option.id, valueObj.id)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addOptionValue(option.id)}
                    className="rounded-full"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                </div>
              </div>
            ))}

            {variantOptions.length > 0 && (
              <Button type="button" onClick={generateVariants} className="rounded-full bg-forest hover:bg-forest-dark text-white">
                Generate Variants
              </Button>
            )}
          </div>

          {/* Variants List */}
          {variants.length > 0 && (
            <div className="space-y-3">
              <Label>Variants ({variants.length})</Label>
              <div className="border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-sand">
                    <tr>
                      <th className="px-3 py-2 text-left">Variant</th>
                      <th className="px-3 py-2 text-left">SKU</th>
                      <th className="px-3 py-2 text-left">Price</th>
                      <th className="px-3 py-2 text-left">Stock</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {variants.map((variant) => (
                      <tr key={variant.id} className="bg-white">
                        <td className="px-3 py-2 font-medium">
                          {Object.values(variant.options).join(' / ')}
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            value={variant.sku}
                            onChange={(e) => updateVariant(variant.id, 'sku', e.target.value)}
                            className="rounded-lg h-8 w-36"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={variant.price}
                            onChange={(e) => updateVariant(variant.id, 'price', parseFloat(e.target.value) || 0)}
                            className="rounded-lg h-8 w-24"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            value={variant.quantity_in_stock}
                            onChange={(e) => updateVariant(variant.id, 'quantity_in_stock', parseInt(e.target.value) || 0)}
                            className="rounded-lg h-8 w-20"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => removeVariant(variant.id)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <Button type="button" onClick={handleSave} className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white">
            Save Variant Changes
          </Button>
        </>
      )}
    </div>
  );
};

export default VariantManager;
